import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { v4 as uuidV4 } from 'uuid'
import Search from '@groovehq/internal-design-system/lib/components/Search/Search'
import { divider } from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import {
  doFetchCannedReplies,
  doFetchCannedReplyCategories,
  doFetchCannedReplyCategoriesAndTemplatesBySearch,
} from 'ducks/cannedReplies/operations'
import {
  selectLastRequestedSearchTerm,
  selectIsLoadingCannedReplyCategories,
  selectHasLoadedTemplates,
  selectMailboxIdForTemplate,
} from 'ducks/cannedReplies/selectors'
import { selectCurrentMailboxId } from 'selectors/mailboxes'
import { debounce } from 'util/functions'
import usePrevious from 'util/hooks/usePrevious'
import { ENTER, TAB } from 'constants/keys'
import KeyboardNavigator from 'components/KeyboardNavigator'
import { Loader } from 'shared/ui'
import { selectMailboxes } from 'selectors/mailboxes_base'

import { styles } from './styles'
import SearchEmpty from '../SearchEmpty'
import Empty from '../Empty'
import DropdownFooter from '../DropdownFooter'
import RepliesInCategory from './RepliesInCategory'
import useHoverMenuItem from './useHoverMenuItem'

const KEYBOARD_NAVIGATOR_KEYS = { select: [ENTER, TAB] }
const recentlyUsedId = uuidV4()

const DropdownMenu = ({
  items,
  onSelect,
  recentlyUsedTemplates,
  hasLoadedReplyCategories,
  visible,
  onVisibleChange,
  setOpenPreview,
  onGetPreview,
  search,
  setSearch,
  setCurrentPreviewTemplateId,
  currentPreviewTemplateId,
  arrowPositionRef,
}) => {
  const searchRef = useRef()
  const containerRef = useRef()
  const menuRef = useRef()
  const searchTerm = useSelector(selectLastRequestedSearchTerm)
  const previousSearch = usePrevious(search)

  const dispatch = useDispatch()
  const [activeItemId, setActiveItemId] = useState()
  const isLoadingCategories = useSelector(selectIsLoadingCannedReplyCategories)
  const hasLoadedTemplates = useSelector(selectHasLoadedTemplates)
  const noSearchResults =
    !!searchTerm.trim() && !items.length && hasLoadedReplyCategories
  const noCannedReplies =
    !items.length &&
    hasLoadedReplyCategories &&
    searchTerm === '' &&
    !isLoadingCategories

  const mailboxId = useSelector(selectCurrentMailboxId)
  const allMailboxesIds = useSelector(selectMailboxes).map(i => i.id)
  const previousMailboxId = useSelector(selectMailboxIdForTemplate)

  const debouncedSearch = useMemo(
    () =>
      debounce(query => {
        dispatch(doFetchCannedReplyCategoriesAndTemplatesBySearch(query))
      }, 500),
    [dispatch]
  )
  const debouncedGetPreview = useMemo(() => debounce(onGetPreview, 200), [
    onGetPreview,
  ])

  const list = useMemo(
    () => {
      if (hasLoadedReplyCategories && !hasLoadedTemplates) return []
      if (!items.length) return []
      if (!recentlyUsedTemplates.length) return items
      const itemsCopy = [...items]
      itemsCopy.unshift({
        id: recentlyUsedId,
        name: 'Recently used',
        total: recentlyUsedTemplates.length,
        type: 'category',
        templates: recentlyUsedTemplates,
      })

      return itemsCopy
    },
    [items, recentlyUsedTemplates, hasLoadedReplyCategories, hasLoadedTemplates]
  )

  const mailboxIds = useMemo(
    () => {
      return mailboxId ? [mailboxId] : allMailboxesIds
    },
    [mailboxId, allMailboxesIds]
  )

  const handleHeaderClick = useCallback(
    item => {
      if (item.type === 'category' && item.name !== 'Recently used') {
        if (!item.loaded && !item.loading) {
          dispatch(doFetchCannedReplies([item.id], searchTerm, mailboxIds))
        }
      }
      searchRef.current.focus()
    },
    [dispatch, searchTerm, mailboxIds]
  )

  const handleShowPreview = useCallback(
    (template, target) => {
      // eslint-disable-next-line no-param-reassign
      arrowPositionRef.current =
        target?.getBoundingClientRect().top -
        containerRef.current?.getBoundingClientRect().top
      setOpenPreview(true)
      setCurrentPreviewTemplateId(template?.id)
      if (currentPreviewTemplateId !== template?.id) {
        debouncedGetPreview(template)
      }
    },
    [
      debouncedGetPreview,
      setOpenPreview,
      currentPreviewTemplateId,
      setCurrentPreviewTemplateId,
      arrowPositionRef,
    ]
  )

  const handleChangeSeach = useCallback(
    e => {
      const { value } = e.target
      setOpenPreview(false)
      setSearch(value)
      debouncedSearch(value)
    },
    [debouncedSearch, setSearch, setOpenPreview]
  )

  useEffect(
    () => {
      const hasData = !searchTerm?.trim() && items.length

      if (!visible) return
      if (search.trim()) return

      if (
        (previousSearch?.trim() && !search.trim()) ||
        (!hasData || !hasLoadedTemplates) ||
        previousMailboxId !== mailboxId
      ) {
        dispatch(doFetchCannedReplyCategories())
        dispatch(doFetchCannedReplies([], '', mailboxIds))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, dispatch, search]
  )

  useEffect(
    () => {
      if (recentlyUsedTemplates.length) {
        setActiveItemId(recentlyUsedId)
      }
    },
    [recentlyUsedTemplates.length]
  )

  useEffect(
    () => {
      if (visible && searchRef.current) {
        searchRef.current.focus()
      }
    },
    [visible]
  )

  const hasSearch = !!searchTerm.trim()

  const handleActivateCategory = useCallback(
    (id, _, target) => {
      setActiveItemId(id)
      setOpenPreview(false)
      // Scroller is jumpy after the items length changes
      // use scrollIntoViewIfNeeded is better
      if (target?.scrollIntoViewIfNeeded) {
        target.scrollIntoViewIfNeeded()
      }
    },
    [setOpenPreview]
  )

  const handleActivateTemplate = useCallback(
    (templateWithCatId, template, _, target) => {
      setActiveItemId(templateWithCatId)
      handleShowPreview(template, target)
      if (target?.scrollIntoViewIfNeeded) {
        target.scrollIntoViewIfNeeded()
      }
    },
    [handleShowPreview]
  )

  const handleActivateByMouse = useCallback(
    (item, id, e) => {
      if (item) {
        handleActivateTemplate(id, item, e, e.target)
      } else {
        handleActivateCategory(id, e, e.target)
      }
    },
    [handleActivateTemplate, handleActivateCategory]
  )

  const { handleMouseEnterItem, handleMouseLeaveItem } = useHoverMenuItem({
    menuElement: menuRef.current,
    onActivate: handleActivateByMouse,
    activeItemId,
    setActiveItemId,
  })

  if (noCannedReplies) {
    return <Empty css={styles.container} onVisibleChange={onVisibleChange} />
  }

  return (
    <div css={styles.container} ref={containerRef}>
      <div css={styles.searchContainer}>
        <Search
          placeholder="Search replies…"
          onChange={handleChangeSeach}
          value={search}
          ref={searchRef}
          css={styles.search}
        />
        {divider}
      </div>
      {isLoadingCategories && (
        <div className="grui p-12">
          <Loader />
        </div>
      )}
      {noSearchResults &&
        !isLoadingCategories && (
          <SearchEmpty onVisibleChange={onVisibleChange} />
        )}
      {!noSearchResults &&
        !isLoadingCategories && (
          <KeyboardNavigator
            activeKey={searchTerm}
            keys={KEYBOARD_NAVIGATOR_KEYS}
            focusElement={searchRef.current}
            css={styles.templatesWrapper}
            // We want to control it to allow more forgiving mouse leave
            activateOnHover={false}
          >
            <div className="grui pb-10" ref={menuRef}>
              {list.map(item => (
                <RepliesInCategory
                  key={item.id}
                  item={item}
                  handleHeaderClick={handleHeaderClick}
                  onSelect={onSelect}
                  active={recentlyUsedId === item.id || hasSearch}
                  handleMouseEnterItem={handleMouseEnterItem}
                  handleMouseLeaveItem={handleMouseLeaveItem}
                  activeItemId={activeItemId}
                  handleActivateCategory={handleActivateCategory}
                  handleActivateTemplate={handleActivateTemplate}
                />
              ))}
            </div>
          </KeyboardNavigator>
        )}
      {<DropdownFooter onVisibleChange={onVisibleChange} />}
    </div>
  )
}

export default React.memo(DropdownMenu)
